// Path: src/hooks/useWorkshopChat.ts
import { useState, useEffect, useRef } from 'react';
import { getSlideChatHistory, clearSlideChatHistory } from '../lib/api';
import { type LocalChatMessage } from '../types/workshop';

export type MessageType = 'design' | 'html' | 'guide' | 'other';

const detectMessageType = (content: string): MessageType => {
  const trimmedContent = content.trim();
  
  if (/^继续开发第\d+页设计/.test(trimmedContent)) {
    return 'design';
  }
  if (/^继续开发第\d+页完整HTML代码/.test(trimmedContent)) {
    return 'html';
  }
  if (/^继续生成第\d+页页边教案/.test(trimmedContent)) {
    return 'guide';
  }
  return 'other';
};

const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    const playTone = (freq: number, startTime: number, duration: number, type: OscillatorType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = freq;
      oscillator.type = type;
      
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    
    // 叮铃铃声音 - 模拟电话铃声
    for (let i = 0; i < 3; i++) {
      playTone(1200, now + i * 0.15, 0.1, 'sine');
      playTone(1400, now + i * 0.15 + 0.05, 0.1, 'sine');
    }
  } catch (e) {
    console.log('Audio not supported');
  }
};

const getSysPromptIdByCourseId = (courseId: number | null): number | null => {
  if (courseId === 5) return 6;
  if (courseId === 6) return 18;
  return null;
};

export function useWorkshopChat(slideId: number, onAIResponseComplete?: (messageType: MessageType, aiResponseText?: string) => void, courseId?: number | null) {
  const [messages, setMessages] = useState<LocalChatMessage[]>([]);
  const [isChatting, setIsChatting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [currentReasoning, setCurrentReasoning] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const aiResponseRef = useRef<string>('');
  const reasoningRef = useRef<string>('');
  const currentMessageTypeRef = useRef<MessageType>('other');
  // 用 ref 保存最新的 onAIResponseComplete 回调，避免 useEffect 闭包陷阱
  // （WebSocket onmessage 在 useEffect([slideId]) 中定义，否则会捕获旧的回调闭包）
  const onAIResponseCompleteRef = useRef(onAIResponseComplete);
  onAIResponseCompleteRef.current = onAIResponseComplete;

  // 1. 加载历史记录
  useEffect(() => {
    if (slideId) {
      getSlideChatHistory(slideId).then(res => {
        // 假设 API 返回的数据已经包含了 files 字段
        setMessages(res.data || []);
      });
    }
  }, [slideId]);

  // 2. WebSocket 连接管理（提取为独立函数，支持 stop_ack 后重连）
  const connectWs = () => {
    if (!slideId) return;
    if (wsRef.current) wsRef.current.close();

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
    
    let protocol, host;
    if (apiBaseUrl.startsWith('/')) {
      const currentUrl = new URL(window.location.href);
      protocol = currentUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      host = currentUrl.host;
    } else {
      try {
        const url = new URL(apiBaseUrl);
        protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        host = `${url.hostname}:${url.port || (url.protocol === 'https:' ? '443' : '80')}`;
      } catch (e) {
        const currentUrl = new URL(window.location.href);
        protocol = currentUrl.protocol === 'https:' ? 'wss:' : 'ws:';
        host = currentUrl.host;
      }
    }
    
    // 获取token
    const getToken = (): string | null => {
      const sessionToken = sessionStorage.getItem('token');
      if (sessionToken) return sessionToken;
      
      const localStorageToken = localStorage.getItem('token');
      if (localStorageToken) {
        try {
          const parsedToken = JSON.parse(localStorageToken);
          if (parsedToken.expiry) {
            if (new Date().getTime() > parsedToken.expiry) {
              localStorage.removeItem('token');
              return null;
            }
            return parsedToken.value;
          }
          return localStorageToken;
        } catch {
          return localStorageToken;
        }
      }
      return null;
    };
    
    const token = getToken();
    const newWs = new WebSocket(`${protocol}//${host}/api/v1/slides/${slideId}/chat${token ? `?token=${token}` : ''}`);
    wsRef.current = newWs;
    
    newWs.onopen = () => {
      console.log('✅ WS Connected');
      setIsConnected(true);
    };
    
    newWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // 可删除提示：reasoning / progress / continuation_waiting（走马灯字幕，不保留，不存入消息）
        if (data.type === 'reasoning' || data.type === 'progress' || data.type === 'continuation_waiting') {
          setCurrentReasoning(data.content || '');
        }
        else if (data.type === 'chunk') {
          // 正文：清除走马灯字幕，累计拼接
          setCurrentReasoning('');
          reasoningRef.current = '';
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant') {
              // 累加到现有 assistant 消息（支持多轮拼接：第二段会拼到第一段 meta 后的消息尾部）
              const newContent = last.content + data.content;
              aiResponseRef.current = newContent;
              return [...prev.slice(0, -1), { ...last, content: newContent }];
            }
            // 兜底：没有 assistant 消息时新建（理论上 sendMessage 已创建 tempAiMsg，不会走到这里）
            aiResponseRef.current = data.content;
            const newMsg: LocalChatMessage = {
              id: 'streaming',
              role: 'assistant',
              content: data.content,
              files: [],
              created_at: new Date().toISOString(),
              input_tokens: 0,
              output_tokens: 0,
              input_money: 0,
              output_money: 0
            };
            return [...prev, newMsg];
          });
        } else if (data.type === 'image') {
          // 🔥 处理 AI 生成的图片：清除走马灯字幕
          setCurrentReasoning('');
          reasoningRef.current = '';
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant') {
              const newFiles = [...(last.files || []), {
                oss_url: data.image_url,
                file_type: 'image_output'
              }];
              return [...prev.slice(0, -1), { ...last, files: newFiles }];
            }
            // 兜底新建
            const newMsg: LocalChatMessage = {
              id: 'streaming',
              role: 'assistant',
              content: '',
              files: [{ oss_url: data.image_url, file_type: 'image_output' as any }],
              created_at: new Date().toISOString(),
              input_tokens: 0,
              output_tokens: 0,
              input_money: 0,
              output_money: 0
            };
            return [...prev, newMsg];
          });
        } else if (data.type === 'meta') {
          setCurrentReasoning('');
          reasoningRef.current = '';
          if (data.error === '429 Too Many Requests' || data.error === '504 Gateway Timeout' || data.error === '400 Bad Request') {
            alert(`请求失败，请稍后再试\n错误原因：${data.error_details || data.error}\n请刷新页面后重试`);
            setMessages(prev => prev.filter(msg => msg.id !== 'streaming'));
            setIsChatting(false);
            aiResponseRef.current = '';
          } else {
            const serverContent = data.full_content || data.content || '';
            const finalContent = aiResponseRef.current || serverContent;

            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.role === 'assistant') {
                const next = [...prev.slice(0, -1), {
                  ...last,
                  id: data.id,
                  content: finalContent,
                  partial: data.partial,
                  partialReason: data.partial_reason,
                  input_tokens: data.input_tokens,
                  output_tokens: data.output_tokens,
                  input_money: data.input_money,
                  output_money: data.output_money,
                  total_money: data.total_money,
                  patched_html: data.patched_html,
                  patch_result: data.patch_result,
                }];
                // 🆕 更新用户消息的真实 ID（参照 WorkshopPlan.tsx 实现）
                // 后端 meta 消息返回 user_message_id，用于替换前端 Date.now() 生成的临时 ID
                const assistantIdx = next.length - 1;
                if (assistantIdx > 0 && next[assistantIdx - 1].role === 'user' && data.user_message_id) {
                  next[assistantIdx - 1] = {
                    ...next[assistantIdx - 1],
                    id: data.user_message_id,
                  };
                }
                return next;
              }
              return prev;
            });

            if (data.partial === true) {
              console.warn(`[useWorkshopChat] 内容可能不完整: partial=true, reason=${data.partial_reason || 'unknown'}`);
            }

            if (data.patch_result) {
              const { success_count, patch_tasks_count, error_messages } = data.patch_result;
              if (success_count < patch_tasks_count) {
                console.warn(`部分补丁未成功应用 (${success_count}/${patch_tasks_count}):`, error_messages);
              }
            }

            aiResponseRef.current = '';
            setIsChatting(false);
            playNotificationSound();
            onAIResponseCompleteRef.current?.(currentMessageTypeRef.current, finalContent);
          }
        } else if (data.type === 'stop_ack') {
          console.log('生成已停止:', data.content);
          setCurrentReasoning('');
          reasoningRef.current = '';
          const stoppedContent = aiResponseRef.current || '';
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant') {
              const next = [...prev.slice(0, -1), {
                ...last,
                id: data.id,
                content: stoppedContent,
                // 写入估算 token/金额
                input_tokens: data.input_tokens ?? 0,
                output_tokens: data.output_tokens ?? 0,
                input_money: data.input_money ?? 0,
                output_money: data.output_money ?? 0,
                total_money: data.total_money ?? 0,
                partial: data.partial ?? true,
                partialReason: data.partial_reason ?? 'user_cancelled',
              }];
              // 更新用户消息的真实 ID
              const assistantIdx = next.length - 1;
              if (assistantIdx > 0 && next[assistantIdx - 1].role === 'user' && data.user_message_id) {
                next[assistantIdx - 1] = {
                  ...next[assistantIdx - 1],
                  id: data.user_message_id,
                };
              }
              return next;
            }
            return prev;
          });
          aiResponseRef.current = '';
          setIsChatting(false);
          playNotificationSound();
          onAIResponseCompleteRef.current?.(currentMessageTypeRef.current, stoppedContent);
          // 关闭旧 WS，重连新 WS，等待新聊天
          setTimeout(() => connectWs(), 100);
        } else if (data.type === 'error') {
          setCurrentReasoning('');
          reasoningRef.current = '';
          alert(`AI Error: ${data.content}`);
          setMessages(prev => prev.filter(msg => msg.id !== 'streaming'));
          setIsChatting(false);
          aiResponseRef.current = '';
        }
      } catch (e) { console.error('WS Error', e); }
    };
    
    newWs.onerror = () => {
      setIsChatting(false);
      setIsConnected(false);
    };
    newWs.onclose = () => {
      setIsConnected(false);
    };
  };

  useEffect(() => {
    connectWs();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [slideId]);

  // 3. 发送消息 (支持 file_urls 和 model)
  const sendMessage = (
    content: string, 
    promptId: number, 
    fileUrls: string[] = [], 
    model: 'gemini' | 'nanobanana' = 'gemini'
  ) => {
    if (!wsRef.current || isChatting || !isConnected) {
      console.warn('WebSocket is not ready');
      return;
    }
    
    currentMessageTypeRef.current = detectMessageType(content);
    
    const optimisticFiles = fileUrls.map(url => ({
      oss_url: url,
      file_type: 'image_input'
    }));

    const userMsg: LocalChatMessage = {
      id: Date.now(), 
      role: 'user', 
      content,
      files: optimisticFiles,
      created_at: new Date().toISOString(), 
      input_tokens: 0,
      output_tokens: 0,
      input_money: 0,
      output_money: 0
    };
    
    const tempAiMsg: LocalChatMessage = {
      id: 'streaming', 
      role: 'assistant', 
      content: '',
      files: [],
      created_at: new Date().toISOString(), 
      input_tokens: 0,
      output_tokens: 0,
      input_money: 0,
      output_money: 0
    };

    setMessages(prev => [...prev, userMsg, tempAiMsg]);
    setIsChatting(true);
    
    const promptIdFromCourse = getSysPromptIdByCourseId(courseId ?? null);
    const finalPromptId = promptIdFromCourse ?? promptId;
    
    wsRef.current.send(JSON.stringify({ 
      content, 
      prompt_id: finalPromptId, 
      clear_history: false,
      file_urls: fileUrls,
      model: model
    }));
  };

  // 4. 清空所有消息
  const clearAllMessages = async () => {
    try {
      await clearSlideChatHistory(slideId);
      setMessages([]); 
      return true;
    } catch (e) {
      console.error("Failed to clear history:", e);
      return false;
    }
  };

  // 5. 停止生成（发送 stop 命令）
  const stopGeneration = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'stop' }));
  };

  return { messages, setMessages, isChatting, isConnected, sendMessage, clearAllMessages, currentReasoning, stopGeneration };
}
